import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/)
const stepUpAssertionSchema = z.string().min(100).max(4096)
const occurredAtSchema = z.string().datetime({ offset: true })

export const payrollDataPurposeV1Schema = z.enum([
  "legal_request",
  "payroll_administration",
  "provider_reconciliation",
  "worker_request",
])

export const payrollRetentionCandidateV1Schema = z
  .object({
    targetHash: sha256Schema,
    retentionClass: z.literal(
      "financial_payroll_permission_approval_reconciliation",
    ),
    occurredAt: occurredAtSchema,
  })
  .strict()

const evidenceBase = {
  retentionVersion: z.literal(1),
  recordId: entityIdSchema,
  policyRevision: entityIdSchema,
  asOf: occurredAtSchema,
  candidates: z.array(payrollRetentionCandidateV1Schema).min(1).max(1000),
  purpose: payrollDataPurposeV1Schema,
  stepUpAssertion: stepUpAssertionSchema,
}

export const payrollDataExportV1Schema = z
  .object({
    ...evidenceBase,
    action: z.literal("sealed_export"),
    sealedExportReference: z.string().trim().min(1).max(255),
    sealedExportSha256: sha256Schema,
  })
  .strict()

export const payrollDataDeletionPreviewV1Schema = z
  .object({
    ...evidenceBase,
    action: z.literal("deletion_preview"),
  })
  .strict()

export const payrollDataDeletionExecuteV1Schema = z
  .object({
    ...evidenceBase,
    action: z.literal("deletion_tombstone"),
    priorEvidence: entityIdSchema,
    sealedExport: entityIdSchema,
    expectedFingerprint: sha256Schema,
  })
  .strict()

const holdBase = {
  retentionVersion: z.literal(1),
  recordId: entityIdSchema,
  holdId: entityIdSchema,
  occurredAt: occurredAtSchema,
  reason: z.string().trim().min(12).max(500),
  purpose: payrollDataPurposeV1Schema,
  stepUpAssertion: stepUpAssertionSchema,
}

export const payrollLegalHoldPlaceV1Schema = z
  .object({
    ...holdBase,
    action: z.literal("hold_place"),
    retentionClass: z
      .literal("financial_payroll_permission_approval_reconciliation")
      .nullable(),
    targetHash: sha256Schema.nullable(),
  })
  .strict()
  .refine(
    (value) => (value.retentionClass === null) !== (value.targetHash === null),
    "Exactly one payroll hold scope is required.",
  )

export const payrollLegalHoldReleaseV1Schema = z
  .object({
    ...holdBase,
    action: z.literal("hold_release"),
  })
  .strict()

export const payrollDataGovernanceResultV1Schema = z
  .object({
    entityType: z.literal("retention_control"),
    entityId: entityIdSchema,
    state: z.enum([
      "sealed_export",
      "deletion_preview",
      "deletion_tombstone",
      "hold_place",
      "hold_release",
    ]),
    version: z.literal(1),
    action: z.enum([
      "sealed_export",
      "deletion_preview",
      "deletion_tombstone",
      "hold_place",
      "hold_release",
    ]),
    purpose: payrollDataPurposeV1Schema,
    stepUpVerified: z.literal(true),
  })
  .passthrough()

export type PayrollDataGovernanceResultV1 = z.infer<
  typeof payrollDataGovernanceResultV1Schema
>
